import React, { useState } from "react";
import Badge from "@mui/material/Badge";
import { Avatar, Typography, InputBase, IconButton } from "@mui/material";
import {
  Search as SearchIcon,
  NotificationsActive as NotificationsIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  WhatsApp as WhatsAppIcon,
} from "@mui/icons-material";

const COLORS = ["#6366F1", "#22C55E", "#06B6D4", "#A855F7"];

const getColorFromString = (str = "") => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % COLORS.length;
  return COLORS[index];
};

const getInitials = (name = "") => {
  const parts = name.trim().split(" ");
  if (parts.length === 0 || !parts[0]) return "";
  const first = parts[0]?.[0] || "";
  const second = parts[1]?.[0] || "";
  return `${first}${second}`.toUpperCase();
};

const ChatSidebar = ({
  chats = [],
  conversations = {},
  activeChatId,
  onSelectChat,
  isDarkMode = false,
  onToggleTheme,
}) => {
  const [searchQuery, setSearchQuery] = useState("");

  // Filtrar los chats en base a la búsqueda
  const filteredChats = chats.filter((chat) =>
    chat.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  return (
    <div style={styles.sidebar}>
      {/* Header del Sidebar */}
      <div style={styles.sidebarHeader}>
        <Typography style={styles.sidebarHeaderTitle}>Chats</Typography>
        {onToggleTheme && (
          <div style={styles.sidebarHeaderActions}>
            <IconButton
              size="small"
              onClick={onToggleTheme}
              style={styles.themeBtn}
              title={
                isDarkMode ? "Cambiar a Modo Claro" : "Cambiar a Modo Oscuro"
              }
            >
              {isDarkMode ? (
                <LightModeIcon style={styles.themeIcon} />
              ) : (
                <DarkModeIcon style={styles.themeIcon} />
              )}
            </IconButton>
          </div>
        )}
      </div>
      {/* Caja de Búsqueda */}
      <div style={styles.searchContainer}>
        <div style={styles.searchWrapper}>
          <SearchIcon style={styles.searchIcon} />
          <InputBase
            placeholder="Buscar o empezar un nuevo chat"
            style={styles.searchInput}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Lista de Chats */}
      <div style={styles.listContainer}>
        {filteredChats.length === 0 ? (
          <div style={styles.emptySearch}>
            <Typography style={styles.emptySearchText}>
              No se encontraron chats
            </Typography>
          </div>
        ) : (
          filteredChats.map((chat) => {
            const chatMessages = conversations[chat.id]?.messages || [];
            const lastMessage = chatMessages[chatMessages.length - 1];
            const isActive = chat.id === activeChatId;

            // Lógica de notificaciones de ChatCabin
            const hasNotification =
              lastMessage &&
              lastMessage.type !== "supervisor-cabin" &&
              chat.id !== activeChatId;

            const initials = getInitials(chat.name);
            const isWhatsAppAdjuster = chat.type === "_WHATSAPP_ADJUSTER_";
            const avatarBg = isWhatsAppAdjuster ? "#22C55E" : "#036CF5";

            // Formatear último mensaje
            let lastMessageText = "";
            let lastMessageTime = "";
            if (lastMessage) {
              lastMessageText =
                lastMessage.class === "image" ? "📷 Imagen" : lastMessage.text;
              if (lastMessage.dataSend) {
                const date = new Date(lastMessage.dataSend);
                lastMessageTime = date.toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                });
              }
            }

            return (
              <div
                key={chat.id}
                style={{
                  ...styles.chatRow,
                  backgroundColor: isActive
                    ? "var(--chat-bg-row-active, #eae6df)"
                    : "transparent",
                }}
                onClick={() => onSelectChat(chat.id)}
              >
                <Avatar
                  style={{
                    ...styles.avatar,
                    backgroundColor: avatarBg,
                  }}
                >
                  {initials}
                </Avatar>

                <div style={styles.rowInfo}>
                  <div style={styles.rowHeader}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        overflow: "hidden",
                        flex: 1,
                        marginRight: "8px",
                      }}
                    >
                      <Typography
                        style={{
                          ...styles.chatName,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {chat.profile.code}
                      </Typography>
                      {isWhatsAppAdjuster && (
                        <WhatsAppIcon
                          style={{
                            color: "#25D366",
                            fontSize: "16px",
                            flexShrink: 0,
                          }}
                        />
                      )}
                    </div>
                    {lastMessageTime && (
                      <Typography style={styles.chatTime}>
                        {lastMessageTime}
                      </Typography>
                    )}
                  </div>

                  <div style={styles.rowBody}>
                    <Typography style={styles.lastMessage} noWrap>
                      {lastMessageText || "Sin mensajes"}
                    </Typography>
                    {hasNotification && (
                      <div style={styles.badgeContainer}>
                        <NotificationsIcon style={styles.notifIcon} />
                        <span style={styles.badgePulse} />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

const styles = {
  sidebar: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    width: "260px",
    backgroundColor: "var(--chat-bg-sidebar, #ffffff)",
    borderRight: "1px solid var(--chat-border, #e9edef)",
  },
  sidebarHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "10px 16px",
    backgroundColor: "var(--chat-bg-header, #f0f2f5)",
    borderBottom: "1px solid var(--chat-border, #e9edef)",
  },
  sidebarHeaderTitle: {
    fontSize: "16px",
    fontWeight: "700",
    color: "var(--chat-text-primary, #111b21)",
    fontFamily: "Raleway, sans-serif",
  },
  sidebarHeaderActions: {
    display: "flex",
    alignItems: "center",
  },
  themeBtn: {
    color: "var(--chat-icon, #54656f)",
  },
  themeIcon: {
    fontSize: "20px",
  },
  searchContainer: {
    padding: "8px 12px",
    backgroundColor: "var(--chat-bg-sidebar, #ffffff)",
    borderBottom: "1px solid var(--chat-border, #e9edef)",
  },
  searchWrapper: {
    display: "flex",
    alignItems: "center",
    backgroundColor: "var(--chat-bg-search-input, #f0f2f5)",
    borderRadius: "8px",
    padding: "4px 10px",
    gap: "10px",
  },
  searchIcon: {
    color: "var(--chat-icon, #54656f)",
    fontSize: "20px",
  },
  searchInput: {
    fontSize: "14px",
    width: "100%",
    color: "var(--chat-text-primary, #111b21)",
    fontFamily: "Raleway, sans-serif",
  },
  listContainer: {
    flex: 1,
    overflowY: "auto",
    backgroundColor: "var(--chat-bg-sidebar, #ffffff)",
  },
  chatRow: {
    display: "flex",
    alignItems: "center",
    padding: "10px 12px",
    cursor: "pointer",
    borderBottom: "1px solid var(--chat-border, #f0f2f5)",
    transition: "background-color 0.15s ease",
    gap: "12px",
    userSelect: "none",
    ":hover": {
      backgroundColor: "var(--chat-bg-row-hover, #f5f6f6)",
    },
  },
  avatar: {
    width: "48px",
    height: "48px",
    fontSize: "16px",
    fontWeight: "600",
    fontFamily: "Raleway, sans-serif",
  },
  rowInfo: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  rowHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    marginBottom: "2px",
  },
  chatName: {
    fontSize: "15px",
    fontWeight: "500",
    color: "var(--chat-text-primary, #111b21)",
    fontFamily: "Raleway, sans-serif",
  },
  chatTime: {
    fontSize: "11px",
    color: "var(--chat-text-secondary, #667781)",
    fontFamily: "Raleway, sans-serif",
  },
  rowBody: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "8px",
  },
  lastMessage: {
    fontSize: "13px",
    color: "var(--chat-text-secondary, #667781)",
    fontFamily: "Raleway, sans-serif",
    flex: 1,
  },
  badgeContainer: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  notifIcon: {
    fontSize: "18px",
    color: "#e53935", // Rojo de notificación
  },
  badgePulse: {
    position: "absolute",
    top: "-2px",
    right: "-2px",
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    backgroundColor: "#e53935",
  },
  emptySearch: {
    padding: "20px",
    textAlign: "center",
  },
  emptySearchText: {
    color: "var(--chat-text-secondary, #667781)",
    fontSize: "14px",
    fontFamily: "Raleway, sans-serif",
  },
};

export default ChatSidebar;
